// Node API client. The request/response shapes below are checked against the
// server's OpenAPI document at build time: `npm run gen` regenerates
// src/generated/api.d.ts from `noded -print-openapi` and the assertions in
// src/contract.ts fail the build if these hand-written types drift.
import type { Platform } from './platforms'
import type { StateName } from './generated/states'
import type { Window } from './schedule'
import type { Vertical } from './vertical'
import type { Rung } from './ladder'

// an rtp destination's 2022-1 FEC matrix (L columns by D rows), and its second path (2022-7)
export interface FEC {
    columns: number
    rows: number
}
export interface RTPPath {
    url: string
    interface?: string
}
// a 2022-7 destination's path as it goes: packets it took, those it could not
export interface RTPPathStatus {
    address: string
    interface?: string
    packets: number
    failed?: number
    last_error?: string
}
export interface SRT {
    latency_ms?: number
    bitrate_k?: number // the most the link carries, kbit/s: sizes SRT's buffers for the latency (25000 if unset)
    passphrase?: string
    pbkeylen?: number
    streamid?: string
}
// a RIST link (kind rist): the profile ("" main), the recovery buffer and
// the main profile's pre-shared key (write-only) with its AES size
export interface RIST {
    profile?: '' | 'simple' | 'main' | 'advanced'
    buffer_ms?: number
    secret?: string
    key_size?: number
}
export interface Input {
    id: string
    name: string
    kind:
        | 'fake'
        | 'decklink'
        | 'srt_listener'
        | 'srt_caller'
        | 'rtmp_listener'
        | 'rtmp_pull'
        | 'url'
        | 'media'
        | 'udp'
        | 'multiview'
        | 'ndi'
        | 'caster' // a browser sends camera and microphone to port (WebRTC)
        | 'audio' // sound only: a sound card (device) or an AES67 stream (audio_in.sdp), its picture black
        | 'rist' // url rist://@:port (we listen) or rist://host:port (we connect)
    caster?: CasterOpts // kind caster: its route and bitrate cap
    audio_in?: AudioSource // kind audio: a sound card or an AES67 stream
    device?: string
    url?: string
    port?: number
    srt?: SRT
    rist?: RIST
    width: number
    height: number
    fps: string
    slate_enabled: boolean
    slate_text?: string
    slate_info?: boolean
    slate_frames?: boolean // with slate_info: the clock per frame with the frame number (15:04:05.37)
    media_id?: string
    overlay_media_id?: string
    overlay_enabled?: boolean
    fallback_media_id?: string
    jitter_ms?: number
    interface?: string // udp: the interface (eth0) or one of its IPv4 addresses the multicast group is joined on
    sources?: string[] // udp: source-specific multicast, the senders taken
    decode?: '' | 'nvdec' // how capture decodes: '' on the CPU, nvdec on an NVIDIA GPU
    decode_gpu?: number
    // an IP input decoded only while something needs its pictures (encodes, outputs, a viewer):
    // passthroughs alone cost no decode; its previews are then key frames, without meters
    skip_decode?: boolean
    // switched off: nothing received or decoded; its encodes and passthroughs are disabled with it
    disabled?: boolean
    deinterlace?: '' | 'auto' | 'on' // capture deinterlaces: off, the frames marked interlaced, every frame
    source_timecode?: boolean // read the source's timecode (SDI RP 188, H.264 or MPEG-2 in a stream)
    // number each frame from the source's timecode, the same on both nodes of
    // a 1+1 pair: what a frame-aligned encode takes its timing from
    frame_aligned?: boolean
    // by PTP: the edge between two frames moved by half a frame, for frames
    // that sat on it (the pair's; central's "frames not aligned")
    frame_shift?: boolean
    captions?: boolean // carry the source's CEA-608/708 captions into its encodes
    scte104?: boolean // a DeckLink input: its SDI feed's SCTE-104 into its encodes as SCTE-35
    st2110?: ST2110In // decklink on a SMPTE 2110 device: the senders' SDPs it receives
    colour?: '' | 'sdr' | 'hlg' | 'pq' // what the picture is: '' follows the source's tags (untagged is SDR)
    bit_depth?: number // the ring's: 0 follows the source, 8 (uyvy422) or 10 (v210)
    multiview?: Multiview
    ndi_name?: string // ndi: the source's name as discovered, "MACHINE (Stream)"; url then its address (ip:port)
    backup?: Backup // its encodes and SDI/NDI outputs switch live to this input while it has no signal
}
// an input's backup: after after_s (0 = 2) seconds without a signal its
// encodes read the backup; back once it has had a signal for
// return_after_s (0 = 10), unless return is manual
export interface Backup {
    input_id: string
    after_s?: number
    return?: '' | 'manual'
    return_after_s?: number
}
// a multiview input: a preset layout and what each tile shows
export interface Multiview {
    layout: string
    tiles: Tile[]
    audio_tile?: number // 1-based; 0 = silence
}
export interface Tile {
    source?: string // "input:<id>", "encode:<id>" or empty
    label?: string // empty = the source's name
    no_label?: boolean // no label on the tile (label is kept for when it shows again)
    vu?: number // meter channels: 0, 2, 8 or 16
    format?: boolean // a line with the source's format
}
export interface Rect {
    x: number
    y: number
    w: number
    h: number
}
export interface Media {
    id: string
    name: string
    kind: 'image' | 'video'
    ext: string
    size: number
    uploaded: string
}
export interface Video {
    codec: string
    gpu: number
    preset?: string
    profile?: string
    level?: string
    rc: string
    bitrate_k: number
    maxrate_k?: number
    bufsize_k?: number
    gop: number
    bframes: number
    width?: number
    height?: number
    scale_algo?: string
    fps?: string
    pix_fmt?: string
    colour?: '' | 'sdr' | 'source' // the stream's: '' SDR in 8 bits (HDR tonemapped), the input's in 10
    master_display?: string // HDR10 (PQ, libx265 and libsvtav1), x265's syntax
    max_cll?: string // "1000,400": MaxCLL,MaxFALL
    vertical?: Vertical // a vertical picture of the input (vertical.ts)
    ladder?: Rung[] // renditions below the picture, for adaptive HLS (ladder.ts)
}
export interface Audio {
    name?: string
    codec: string
    bitrate_k: number
    sample_rate?: number
    layout: string
    channel_map: number[]
}
// passthrough: the IP input's stream copied to the destinations, not re-encoded (video.bitrate_k then sizes the delay buffer)
export interface Encode {
    id: string
    name: string
    input_id: string
    enabled: boolean
    video: Video
    audio: Audio[]
    advanced?: string
    passthrough?: boolean
    // what it sends, decoded and judged, each check opted in (live): 10 s
    // of a black picture, the same picture or a silent track is degraded
    check_black?: boolean
    check_frozen?: boolean
    check_silent?: boolean
    probe_off?: boolean // an older node's switch (checks on by default then); dropped by a newer one
    // its own slate (toggles live) and what it sends while its input has no signal
    slate_enabled?: boolean
    slate_text?: string
    on_loss?: '' | 'black' | 'picture' | 'stop'
    loss_media_id?: string
    // Twitch Enhanced Broadcasting: Twitch picks the renditions at every start
    // (video then only gives the GPU, audio[0].channel_map the stereo pair)
    twitch?: TwitchEB
    // ad markers: an SCTE-35 stream in what it sends, and the breaks given
    // (api.cue); on or off restarts the encode, the rest changes live
    scte35?: SCTE35
}
export interface SCTE35 {
    mode: 'splice_insert' | 'time_signal'
    preroll_ms?: number // how long before its break a cue goes out; 0 = 4000
    breaks?: Window[] // scheduled breaks: the cue the preroll before each start, its length the window's
    ignore_source?: boolean // an IP input's own cues are not carried into it (by default they are)
}
export interface TwitchEB {
    stream_key: string // the channel's; read back as the secret placeholder
    max_tracks?: number // video renditions Twitch may ask for, 1..5; 0 = Twitch's choice
    max_bitrate_k?: number // all of them together; 0 = Twitch's choice
    protocol?: '' | 'rtmps' | 'rtmp'
}
export interface Destination {
    id: string
    name: string
    encode_id: string
    kind:
        | 'rtmp_push'
        | 'srt_caller'
        | 'file'
        | 'rtmp_pull'
        | 'srt_listener'
        | 'udp'
        | 'twitch_eb'
        | 'hls_push'
        | 'hls_pull'
        | 'rtp'
        | 'rist'
    enabled: boolean
    delay_seconds: number
    url?: string
    // in a 1+1 pair: where the partner node sends this (a platform's backup
    // ingest); this node sends to url. Central swaps the two for the partner.
    partner_url?: string
    // in a 1+1 pair, sent by one node only: this one, or its partner (this
    // one holds it as disabled); absent, both. Twitch takes one stream per key.
    pair_only?: '' | 'this' | 'partner'
    srt?: SRT
    rist?: RIST
    pull_auth?: PullAuth
    max_clients?: number
    port?: number
    stream?: string
    path?: string
    segment_seconds?: number
    keep_days?: number
    container?: '' | 'ts' | 'mp4'
    open_ended?: boolean
    ttl?: number // udp: multicast hops (0 = 16)
    interface?: string // udp, rtp: local IPv4 address to send from
    fec?: FEC // rtp: SMPTE 2022-1 column and row FEC
    path2?: RTPPath // rtp: a second path, every packet on both (SMPTE 2022-7)
    // the broadcaster feed it was filled from on central (a label; a copy)
    origin?: DestinationOrigin
    schedule?: Window[] // when the node starts and stops it (schedule.ts)
    adaptive?: boolean // hls_push: every rendition of its encode's ladder, under a master playlist
    hls_cues?: '' | 'cue_out' | 'daterange' // hls_push: the SCTE-35 cues as playlist tags (node/hlscues.go); '' none
}
export interface DestinationOrigin {
    broadcaster: number
    broadcaster_name: string
    feed: string
    feed_name: string
}
// an encode's settings kept on central, copied into an encode when picked
export interface EncodeTemplate {
    id: number
    name: string
    video: Video
    audio: Audio[]
    advanced?: string
    updated_by: string
    updated_at: string
}
// what a template save sends: central stamps who and when
export type TemplateBody = Omit<EncodeTemplate, 'updated_by' | 'updated_at'>
export interface Group {
    id: string
    name: string
    destinations: string[]
    schedule?: Window[] // when the node starts and stops its destinations
}
export interface Output {
    id: string
    name: string
    input_id: string // '' with source
    source?: string // decklink, ndi: "encode:<id>" or "destination:<id>", played in place of an input
    kind: 'decklink' | 'ndi' | 'hdmi' | 'alsa' | 'aes67' | 'null'
    ndi_name?: string // ndi: the source's name, shown as "MACHINE (name)"; '' = the output's name
    device?: string // decklink: device; hdmi: connector (HDMI-A-1), '' = the first connected
    enabled: boolean
    display_mode?: string // hdmi: WxH or WxH@Hz, '' = 1080p when the screen has it, else its preferred
    audio_device?: string // hdmi: as mpv lists it, '' = the connector's own HDMI audio
    on_loss?: '' | 'black' | 'slate' // hdmi: '' freezes the last frame
    slate_text?: string
    advanced?: string // hdmi: extra mpv options
    buffer_ms?: number // decklink: what the card holds before it plays (its delay); 0 = 100
    st2110?: ST2110Out // decklink on a SMPTE 2110 device: where its flows are sent
    // alsa, aes67: the input's channels it carries, in order (1-16; none: 1+2); aes67 at most 8
    audio_channels?: number[]
    aes67?: AES67Out // aes67: where the stream goes
}
// where an AES67 output sends (config.AES67Out)
export interface AES67Out {
    address: string // multicast group (239.69.x.y as Dante uses) or a receiver's address
    port?: number // 0 = 5004
    interface: string // the node's IPv4 address on the audio network
}
// the node's PTP follower for its AES67 outputs (node.PTPStatus)
export interface PTPStatus {
    interface: string
    domain: number
    running: boolean
    error?: string
    grandmaster?: string // 00-1D-C1-FF-FE-12-34-56; absent while none is heard
    offset_ns?: number // the system clock against the grandmaster
}
// where the node finds NDI sources and how its NDI outputs send
export interface NDISettings {
    discovery_servers?: string[]
    extra_ips?: string[]
    no_multicast_recv?: boolean
    multicast_send?: boolean
    multicast_prefix?: string
    multicast_ttl?: number
}
export interface Template {
    id: string
    name: string
    kind: 'rtmp_push' | 'srt_caller'
    url: string
    srt?: SRT
}
export interface NodeIdentity {
    name: string
    site?: string
    tags?: string[]
}
export interface Config {
    version: number
    mode?: '' | 'player' // player: IP inputs on an HDMI screen, no encodes
    node: NodeIdentity
    max_buffer_seconds: number
    full_buffers?: boolean // every encode's ring holds max_buffer_seconds: a delay change jumps at once
    inputs: Input[]
    encodes: Encode[]
    destinations: Destination[]
    templates?: Template[]
    groups?: Group[]
    outputs?: Output[]
    storage?: Storage[] // network shares for recordings, mounted at /mnt/ese/<id>
    ndi?: NDISettings
    ptp?: PTPSettings // the plant's clock for AES67 outputs and frame-aligned inputs; domain 0 by default
    st2110?: ST2110 // the node's DeckLink IP cards: PTP, NMOS registry, ports
    monitor_lock?: MonitorLock // set while monitoring is locked: changed only by api.lockMonitoring/unlockMonitoring
    machine_acks?: MachineAck[] // machine problems acknowledged on the node: changed only by api.ackProblem/unackProblem
    update_hold?: UpdateHold // set while the node's updates are held: changed only by api.holdUpdates/liftUpdateHold
}
// the node's PTP follower (config.PTP): its domain, and its address on the
// PTP network for frame-aligned inputs when no AES67 output names one
export interface PTPSettings {
    domain?: number
    interface?: string
}
// monitoring locked on a node (node monitorlock.go): who locked it and when
export interface MonitorLock {
    by: string
    since: string
}
// the node's software updates held (node updatehold.go): an update from the
// interface or an upload is refused until it is lifted or lapses at until
export interface UpdateHold {
    by: string
    since: string
    until?: string // when it lapses by itself; none: until somebody lifts it
    note?: string // why
}
// a machine problem acknowledged on the node (node machineack.go): the
// machine's state does not stand on it while it lasts; it lapses once the
// problem has been gone for 10 minutes
export interface MachineAck {
    key: string
    by: string
    at: string
    note?: string
}
// one of the machine's problems now: a stable key (no counts in it), a
// warning (degraded) or a fault (hardware_fault), and whether it is acked
export interface MachineProblem {
    key: string
    severity: 'warning' | 'fault'
    text: string
    acked?: boolean
}
export interface Storage {
    id: string
    name: string
    kind: 'smb' | 'nfs' | 'disk'
    server?: string
    share?: string
    username?: string
    password?: string
    domain?: string
    uuid?: string
    fstype?: string // disk: an internal or USB drive, by its filesystem's UUID
}
export interface BlockDevice {
    path: string
    size_gb: number
    fstype: string
    label: string
    uuid: string
    model: string
    transport: string
    removable: boolean
    mountpoints: string[]
    system: boolean
}
export interface StorageStatus extends Snapshot {
    id: string
    name: string
    mounted: boolean
    writable: boolean
    free_gb: number
    total_gb: number
}
export interface FsDir {
    path: string
    parent?: string
    dirs: string[]
    writable: boolean
    free_gb: number
    storage?: string
}

export interface Snapshot {
    state: StateName // the node's state table (generated/states.ts, from internal/states)
    error?: string
    since: string
    restarts: number
}
export interface EncodeStatus extends Snapshot {
    fps: number
    bitrate_k: number
    frames: number
    dropped_frames: number
    falls_behind?: boolean // frames lost within the last minute: the encoder does not keep up
    ring_bytes: number
    ring_capacity: number
    ring_seconds: number
    buffer_k?: number // a passthrough's: the kbit/s its ring holds its longest delay plus 15 s at (set, 25000 assumed, or measured)
    cpu_cores: number // its ffmpeg, in cores (1 = one core busy)
    gpu?: GPUUse // its share of its GPU (NVENC encodes)
    quantizer?: number // the video's (none for a copy): rises when the bitrate is short for the picture
    ffmpeg_dup?: number // frames ffmpeg duplicated itself
    ffmpeg_drop?: number // and dropped
    key_interval_s?: number // between key frames as they came out
    twitch?: TwitchStatus // a Twitch Enhanced Broadcasting encode: what Twitch asked for
    reading?: string // the input it reads when not its own (its input's backup)
    captions?: string // its input's captions: "carried", or why not (none when the input has none)
    caption_frames?: number // frames it carried captions on
    etr290?: ETR290Counts // its stream against TR 101 290 since the encode started (output probes)
    etr290_last?: string // the last error, 2.3a's PCR spacing aside
    probe?: ProbeStatus // what it sends, decoded: absent while it has seen nothing (probe_off, starting)
    break?: BreakStatus // the ad break its cues said: coming, on or ending
    scte35?: string // why its cues do not go out (none when they do)
    source_cues?: number // a passthrough: SCTE-35 sections its source sent, which it carries
    source_cue_last?: string // and the last cue of them read
    // an encode of a frame-aligned input: on its frame numbers (timestamps and
    // key frames as its 1+1 partner's), or waiting for them
    frame_aligned?: '' | 'numbers' | 'waiting'
}
// an encode's ad break (node cues.go), from its start cue
export interface BreakStatus {
    event_id: number
    mode: 'splice_insert' | 'time_signal'
    starts: string
    ends?: string // none: until it is ended
    ending?: boolean // its end cue sent
}
export interface CueRequest {
    type: 'out' | 'in' // a break starts, or the one on ends
    duration_s?: number // out: its length; 0 = until an in cue
    preroll_ms?: number // the encode's when absent
}
export interface CueAnswer {
    event_id: number
    mode: 'splice_insert' | 'time_signal'
    pts: number
    at: string // when it splices
}
// what an encode's probe saw (node probe.go): seconds black, the same
// picture, each track silent; 10 s of one makes the encode degraded
export interface ProbeStatus {
    black_s?: number
    frozen_s?: number
    silent_s: number[]
    at: string // its last key frame decoded
}
// TR 101 290's checks on an encode's stream (node tsring.ETR290): each
// item's errors; 2.3a is counted, not warned of
export interface ETR290Counts {
    sync_loss: number // 1.1
    sync_byte: number // 1.2
    pat: number // 1.3
    continuity: number // 1.4
    pmt: number // 1.5
    pid: number // 1.6: a stream the PMT lists missing for 5 s
    transport: number // 2.1
    crc: number // 2.2
    pcr_repetition: number // 2.3a: PCRs over 40 ms apart (a stream under 25 fps cannot keep it)
    pcr_discontinuity: number // 2.3b
}
// SMPTE 2110 through a DeckLink IP card (config st2110.go): an input's
// senders' SDPs, an output's flow addresses (address:port), the node's
// cards
export interface ST2110In {
    video_sdp: string
    audio_sdp?: string
    anc_sdp?: string
}
export interface ST2110Out {
    video: string
    audio?: string
    anc?: string
}
export interface ST2110 {
    ptp_domain?: number // the card's own (127) when unset
    ptp_follower_only?: boolean
    nmos_registry?: string // host:port; empty: the card finds one
    ports?: ST2110Port[]
}
export interface ST2110Port {
    device: string
    index: number // its connector, from 0
    dhcp?: boolean
    address?: string
    mask?: string
    gateway?: string
}
// a SMPTE 2110 device (node machine.DeckLinkIP): its connectors, PTP,
// NMOS registry and IP flows
export interface DeckLinkIP {
    device: string // as an input or output names it
    ethernet: {
        index: number
        link: string // down, unbound (up, no address), bound
        mbps: number
        dhcp: boolean
        mac: string
        address: string
        mask: string
        gateway: string
        rx_packets: number
        rx_dropped: number
        out_video?: string
        out_audio?: string
        out_anc?: string
    }[]
    ptp: { domain: number; grandmaster?: string } // no grandmaster: not locked
    nmos_registry?: string
    flows: { id: number; direction: string; type: string; sdp?: string }[]
}
// the configuration Twitch gave at the encode's start
export interface TwitchStatus {
    config_id: string
    at: string
    ingest: string // its host (the URL carries the stream key)
    renditions: { width: number; height: number; fps: number; bitrate_k: number; codec: string; preset?: string }[]
    audio_k: number
    warning?: string // Twitch's, in its words
    fallback?: string // why the node sends its own single rendition: Twitch's configuration service could not be reached
    encode_percent?: number // of the GPU's encoder, as Twitch expects
}
// one process's share of its GPU, in percent (nvidia-smi pmon)
export interface GPUUse {
    gpu: number
    sm: number // 3D/compute
    enc: number // NVENC
    dec: number // NVDEC
}
export interface PullAuth {
    user: string
    pass: string
}
// an SRT connection seen from its end (ffcmd.SRTStats): an input receives, a destination sends
// an SRT or RIST link (protocol "rist"): RIST's missing packets are lost,
// its recovered ones retransmitted, those lost for good dropped
export interface SRTStats {
    protocol?: 'rist'
    rtt_ms: number
    link_mbps: number // SRT's estimate of what the path carries
    rate_mbps: number // the last second, retransmissions included
    packets: number // received or sent since the connection began
    lost: number // went missing on the way
    retransmitted: number
    dropped: number // given up on: too late to play or send (what a viewer sees)
    undecrypted?: number // a passphrase that does not match
    buffer_ms: number
    latency_ms: number // agreed by both ends
    in_flight?: number // sender: not yet acknowledged
    quality?: number // RIST: librist's, the last second, in percent
}
// a TCP connection from its end (node/sockets.go): an input receives, a destination or pull server sends
export interface TCPStats {
    peer: string
    rtt_ms: number
    min_rtt_ms: number
    rate_mbps: number // the last second: acknowledged (sending) or received
    segments: number
    retransmitted?: number // sending: segments sent again
    queue_bytes?: number // sending: in the kernel, not yet acknowledged
    queue_seconds?: number // that, in seconds of the stream
}
// what the kernel says of an entity's sockets
export interface SocketStats {
    tcp?: TCPStats
    udp_drops?: number // datagrams the kernel dropped (a full socket buffer), since the entity started
    overruns?: number // ffmpeg's own UDP buffer overran
}
// MPEG-TS packets that arrived broken, since the input started: TR 101
// 290's 1.4 and 2.1, as ffmpeg's demuxer counts them in the feed
export interface TSErrors {
    continuity: number
    tei?: number
}
export interface DestStatus extends Snapshot {
    delay_seconds: number
    bytes_sent: number
    clients: number
    port?: number
    pull_running?: boolean
    pull_error?: string
    pull_latency_ms?: number // srt_listener: the node's side of its readers' latency (20 on our mediamtx, 120 on stock)
    srt?: SRTStats // srt_caller: the connection now
    socket?: SocketStats // its sender's: an RTMP push's TCP connection
    free_gb?: number // file: free space where it records
    hours_left?: number // and how long that lasts at the encode's bitrate
    hls?: HLSPushStatus // hls_push: its uploads
    paths?: RTPPathStatus[] // rtp with a second path (2022-7): each path
}
// an hls_push destination's uploads, this run of its sender
export interface HLSPushStatus {
    segments: number // put
    skipped: number // gone before they could be put (the uploads fell behind)
    waiting: number // written, not put yet
    last_put_s: number // how long the last segment took to put
    last_at: string // when the last playlist was put
    endpoint: string // the host the files go to
}
// a stereo pair's loudness, LUFS: momentary (400 ms), short-term (3 s), integrated (gated, since the input started)
export interface Loudness {
    m: number
    s: number
    i: number
}
export interface InputStatus extends Snapshot {
    received: number
    repeated: number
    source?: string
    peak_db?: number[]
    loudness?: Loudness[] // EBU R128 per stereo pair (channels 0+1, 2+3, …); -100: nothing to measure
    timecode?: string // the source's, of the frame published last (source_timecode)
    // a frame-aligned input's: numbered from its timecode, from PTP, or not (none)
    frame_index?: '' | 'timecode' | 'pts' | 'ptp' | 'none'
    frame_edge_ms?: number // by PTP: how far its frames sit from the edge where two nodes could number them one apart
    // the frame published last: its number and when (ns since 1970, the node's clock and PTP's where it follows one)
    frame_number?: number
    frame_number_at?: number
    frame_number_ptp?: number
    frame_phase?: number // by PTP: their phase to the grid frame they took, a signed fraction of a frame
    cpu_cores: number // its capture (decode), receiver and loop, in cores
    gpu?: GPUUse // its decoder's GPU share (decode nvdec)
    srt?: SRTStats // an SRT input's connection now, as its receiver sees it
    ts_errors?: TSErrors
    socket?: SocketStats // its receiver's: a TCP source, UDP drops
    ndi?: NDIStats // an NDI input's receiver
    capture_overruns?: number // DeckLink frames ffmpeg's queue had no room for
    caster?: CasterStats // a caster input's connection (or the last one's)
    caster_error?: string // why its gateway does not run
    return_encoder?: string // what encodes its return feed ("NVENC on GPU 0", "x264", why x264)
    return_shared_with?: string[] // the other caster inputs its return encode serves
    failover?: FailoverStatus // an input with a backup
}
export interface FailoverStatus {
    backup: string // the backup input's id
    on_backup: boolean // its encodes and outputs read the backup now
    since?: string
    held?: boolean // an operator chose the backup: no automatic return
}
// a caster input's link: route "" automatic (direct, central's relay when that fails), "relay" always, "direct" never
export interface CasterOpts {
    route?: '' | 'relay' | 'direct'
    bitrate_k?: number // video cap, kb/s; 0 = 8000
    return_input?: string // the input the caster sees and hears back; "" none
    return_from?: string // another caster input whose return feed this one takes (its settings copied in by the node)
    return_pair?: number // the pair the caster hears, 0-7 (0: channels 1+2): production's mix for them
    latency?: '' | 'low' // low: one resend at most, one frame waiting, frames as they come (nearby casters)
    return_height?: number // 0 = 720
    return_bitrate_k?: number // 0 = 2500
    return_fps?: '' | 'full' // "" half the input's rate above 30 fps
    return_encoder?: '' | 'cpu' | 'gpu' // "" automatic: NVENC with a session to spare, else x264
    // comms on top of the return (live): an input's pair, one channel of it
    // in both ears or both, how they sit on the programme, their level
    comms_input?: string
    comms_pair?: number
    comms_mono?: '' | 'left' | 'right'
    comms_mode?: '' | 'duck' | 'mix' | 'split'
    comms_level_db?: number
    // the caster may talk back, off air (live): a button on their page,
    // their voice on this input's channels 3+4
    talkback?: boolean
    // a player input: any number of players connect, the studio takes one
    // on air; nothing goes back to them (no return feed, comms or talkback)
    players?: boolean
}
// how a caster hears comms (internal/caster/comms.go)
export interface CommsMix {
    on: boolean
    pair: number
    mono?: '' | 'left' | 'right'
    mode?: '' | 'duck' | 'mix' | 'split'
    level_db?: number
}
// central's TURN server (central/turn.go)
export interface TURNInfo {
    configured: boolean // TURN_URLS and TURN_SECRET set
    ice_servers: { urls: string[]; username?: string; credential?: string }[] // fresh, for the check
    vm?: { project: string; zone: string; name: string; network: string; tags: string[] } // central's VM (superadmins)
    can_open: boolean // a superadmin on Google Cloud with the sign-in set up
    signed_in: boolean
    rule?: string
    email?: string
}
// a local caster link (node castlink.go): made on the node, for a caster on
// its own network; its urls only in the answer that made it
export interface CastLink {
    id: string
    input_id: string
    name: string
    created_by?: string
    created_at: string
    expires_at: string
    urls?: string[]
}
// an input's local links; port 0: the node serves none
export interface CastLinks {
    port: number
    links: CastLink[]
}
// an invite link (central): someone without an account casts to one caster input with it
export interface CasterInvite {
    id: number
    node_id: string
    input_id: string
    name: string
    created_by?: string
    created_at: string
    expires_at: string
    revoked_at?: string
    last_used_at?: string
    url: string
}
// one track of a caster's connection, since it connected
export interface CasterTrack {
    packets: number
    bytes: number
    reordered: number // arrived after a later one: resent in time
    lost_for_good: number
    late: number
    gaps: number
    max_hold_ms: number // the longest a gap held the stream
    from_redundancy?: number // audio: rebuilt from the next packet's copy (RED)
    nacks?: number // video: resends asked for
    delay_ms?: number // the playout delay now: 0 on a clean line (CST-1)
}
// a caster input's WebRTC connection (internal/caster)
export interface CasterStats {
    connected: boolean
    since: string
    remote?: string // the caster's address as the node sees it
    relay: boolean // through TURN on central
    rtt_ms: number
    wait_ms: number // how long a gap may hold the stream
    codec?: string
    key_frame_requests: number
    video: CasterTrack
    audio: CasterTrack
    return_live: boolean // the return feed reaches the caster's connection
    return_pair: number // the audio pair the caster hears (0: channels 1+2)
    return_audio_packets?: number
    return_paused?: boolean // the studio paused it
    return_bytes?: number // return video sent since the gateway started
    return_refused?: boolean // the caster's page offered to send only: it gets nothing until reloaded
    comms_live?: boolean // comms come from the node and are on
    comms_packets?: number // comms sent the caster
    comms?: CommsMix // production's mix; absent: the caster's page takes no comms (an older page)
    talkback?: boolean // production lets the caster talk back
    talkback_live?: boolean // the page's talkback track reaches the node (absent: an older page)
    talkback_packets?: number
    name?: string // who is casting: the invite's name
    page?: CasterPage // what the caster's page reports
    cue?: CasterCue // the studio's last cue
    limits?: CasterLimits // the studio's caps now
    session?: string // the connection's id: a command for this one names it
    width?: number // the caster's picture, from its stream
    height?: number
    fps?: number
    // a player input: every player connected (on air or not), and for each
    // whether it is on air or goes on at its next key frame
    players?: CasterStats[]
    on_air?: boolean
    taking?: boolean
}
// what a caster's page reports over its data channel (internal/caster/control.go)
export interface CasterPage {
    muted: boolean
    muted_by?: 'studio' | 'caster'
    talking?: boolean // holding talkback: their microphone to production, off air
    remote: boolean // the caster lets the studio switch camera and microphone
    cam?: string // device ids in use
    mic?: string
    cams?: { id: string; label: string }[] // only with remote
    mics?: { id: string; label: string }[]
    health?: 'good' | 'fair' | 'poor' // the page's own reading of its line
    health_why?: string
    encoder?: string // the browser's H.264 encoder (e.g. OpenH264, ExternalEncoder (…))
    hw_encoder?: boolean // a hardware one
}
export interface CasterCue {
    id: number
    text: string
    sent: string
    seen?: string // the caster dismissed it
}
// 0 or absent: the input's own
export interface CasterLimits {
    max_bitrate_k?: number
    height?: number
    fps?: number
}
// a studio command to the connected caster; each field present is done
export interface CasterControl {
    cue?: string
    mute?: boolean
    limits?: CasterLimits
    cam?: string
    mic?: string
    return?: boolean // the return feed on or paused (live)
    // set by the node from the input's config, not sent from the page
    pair?: number // the return pair the caster hears
    comms?: CommsMix // how the caster hears comms
    talkback?: boolean // the caster may talk back
    studio?: string // the return feed's name, '' none (the page shows it by this)
    session?: string // the connection it is for: '' the one on air (a player input: any player)
}
// an NDI input's receiver since capture started
export interface NDIStats {
    video: number
    audio: number
    video_dropped: number // capture did not take them in time
    audio_dropped: number
    queued: number // video frames waiting
    connections: number // 0: not connected to the source
}
export interface DiskStatus {
    path: string
    used_pct: number
    free_gb: number
    total_gb: number
}
export interface GPUStatus {
    index: number
    name: string
    util_pct: number
    enc_pct: number
    mem_pct: number
    temp_c: number
    sessions: number
    driver: string
    nvenc_max: number
    nvenc_no_limit?: boolean
    nvenc_note?: string // NVENC sessions the GPU takes at once (measured by the node; 0 = not yet)
    // health (absent when the GPU does not say): power, why its clocks are held down, ECC
    power_w?: number
    power_limit_w?: number
    throttle?: string[]
    ecc_uncorrected?: number
    ecc_corrected?: number
    remap_pending?: boolean
    remap_failed?: boolean
}
export interface Specs {
    cpu_model: string
    cores: number
    os: string
    kernel: string
    ffmpeg: string
    cloud?: CloudInfo // the cloud the machine runs in; absent on bare metal
}
// CloudInfo: which cloud a node runs in, from DMI and the instance metadata
export interface CloudInfo {
    provider: string // "gcp" or "aws"
    zone?: string
    machine_type?: string
    instance_id?: string
}
export const cloudNames: Record<string, string> = { gcp: 'Google Cloud', aws: 'AWS' }
// cloudShort: "GCP", "AWS" for a chip
export const cloudShort = (c: CloudInfo) => c.provider.toUpperCase()
export interface DeckLink {
    name: string
    input: boolean
    output: boolean
}
export interface TempStatus {
    name: string
    label: string
    c: number
}
export interface NetInterface {
    name: string
    mac: string
    up: boolean
    speed_mbps?: number // the link's speed; none when down or unknown (Wi-Fi, some VMs)
    addresses: string[] // 10.1.2.3/24
}
export interface ClockStatus {
    synced: boolean // the kernel says NTP keeps it
    error_ms: number // the kernel's estimated error
    offset_ms?: number // from its time server, as the sync daemon last measured it
    server?: string
    daemon?: 'chrony' | 'timesyncd'
    managed: boolean // false in a container or WSL: the host keeps the clock
}
export interface NetSettings {
    interface: string
    dhcp?: boolean // an address from DHCP; else the static ones
    addresses?: string[] // with prefix: 10.0.0.5/24
    gateway?: string
    dns?: string[]
}
export interface SystemSettings {
    ntp: string[] // time servers; empty: the OS's own
    network: NetSettings[] // interfaces set from the page; any other: the OS's own
}
export interface SystemStatus {
    settings: SystemSettings
    applied?: string // when root last applied them
    error?: string
    daemon?: string
    network: NetSettings[] // root's: the network settings kept
    pending?: string // a network change is on trial until then, unless kept
    trial?: NetSettings[] // the settings on trial (a reboot during one undoes them)
}
export interface MachineStatus extends Snapshot {
    cpu_pct: number
    mem_pct: number
    mem_used_gb: number
    mem_total_gb: number
    disks: DiskStatus[]
    net_rx_kbps: number
    net_tx_kbps: number
    gpus: GPUStatus[]
    core_pct: number[]
    cpu_temp_c: number
    temps: TempStatus[]
    hostname: string
    specs: Specs
    decklink: DeckLink[]
    decklink_note?: string
    decklink_cards: {
        // one per profile group: a whole 8K Pro, each connector pair of a Duo 2 or Quad 2
        name: string
        profile: string
        profiles: string[]
        devices?: string[] // the group's devices, name first; absent from an older decklink-profile
        reference?: 'unlocked' | 'locked' // its reference (genlock) input; absent: none
        reference_mode?: string // what it locked to: "1080i59.94", "PAL"
    }[]
    decklink_ip?: DeckLinkIP[] // SMPTE 2110 devices (DeckLink IP cards), from decklink-ip
    addresses: string[]
    interfaces: NetInterface[] // every network interface: name, MAC, addresses with prefix
    clock: ClockStatus // NTP sync: the slate's clock and every timestamp hang on it
    displays: { connector: string; card: string; connected: boolean; modes: string[] }[] // a player's
    audio_devices: { name: string; description: string }[] // a player's, as mpv lists them
    sound_cards: { device: string; name: string }[] // capture devices an audio input can take
    sound_outputs: { device: string; name: string }[] // playback devices an audio output can take
    sound_card_note?: string // why none can be taken (ffmpeg without ALSA)
    public_ip: string
    gpu_note?: string // why an NVIDIA GPU shows no figures (nvidia-smi's own words)
    problems: MachineProblem[] // every problem now, faults first, acked ones marked (absent from a node before 2026-10-17)
    hardware?: string[] // the warnings not acked (a Pi's undervoltage, throttling): the machine is degraded
    faults?: string[] // the faults not acked (errors the hardware could not correct, a read-only filesystem): hardware_fault
    fans?: { name: string; rpm: number }[] // hwmon's fans; absent without hwmon (VMs)
    kernel_note?: string // why the kernel log's hardware errors cannot be read
    // the drives' health from root's probe; why it is missing or old
    drives?: {
        device: string
        model: string
        passed: boolean
        temp_c?: number
        used_pct?: number
        power_on_h?: number
        media_errors?: number
        last_test?: string // its last self-test: "short test: completed without error"
        last_test_ago_h?: number // powered-on hours ago
    }[]
    memory_modules?: {
        locator: string
        size_gb: number
        type: string
        speed_mts?: number
        configured_mts?: number
        part?: string
    }[]
    memory_ecc?: string // the memory's error correction as dmidecode says it ("None", "Multi-bit ECC")
    drive_note?: string
    notes?: string[] // worth knowing, no problem: a reboot waiting for updated packages
    reboot_reason?: string // why a reboot is wanted (updated packages, DeckLink firmware, a module not loaded); absent when none
}
// what a reboot of the node would do (GET /api/machine/reboot, the answer of the POST too)
export interface RebootStop {
    kind: string // destination, output or caster
    id: string
    name: string
}
export interface RebootPlan {
    available: boolean
    refused?: string // why now is no time: the button is off with this text
    stops: RebootStop[] // what is outputting: each is named in the dialog, and the node checks the list
    watching: { viewers: number; relays: number }
    running: { encodes: number; inputs: number }
    up_since: string
    root?: { state: string; at: string; reason?: string; action?: string } | null // root's last answer, for as long as its status file exists
    hold?: UpdateHold | null // the node's updates held: the reboot goes ahead, the dialog says so (the shutdown's plan is the same)
}
export interface OutputStatus extends Snapshot {
    reading?: string // sdi, ndi: the input it reads when not its own (its input's backup)
    fps: number
    frames: number
    dropped_frames: number
    decoder?: string // hdmi: codec, hardware or software
    cpu_cores: number
    srt?: SRTStats // hdmi: the stream's SRT link
    ts_errors?: TSErrors
    socket?: SocketStats // hdmi: the relay's sockets
    av_sync_ms?: number // hdmi: sound ahead of picture (negative: behind)
    buffer_s?: number // hdmi: stream mpv holds before playing
    delayed_frames?: number // hdmi: frames shown late
}
export interface FFOption {
    name: string
    type?: string
    help: string
    ctx?: string
}
export interface Status {
    time: string
    version: number
    uptime_seconds: number
    features: string[] // what the node can (internal/node/features.go); absent from a node before the list
    inputs: Record<string, InputStatus>
    encodes: Record<string, EncodeStatus>
    destinations: Record<string, DestStatus>
    outputs: Record<string, OutputStatus>
    machine: MachineStatus
    pull_ports: { rtmp: number; srt: number; hls: number } // defaults for pull destinations without their own port
    storage: StorageStatus[]
    central?: CentralStatus // absent until the node is enrolled
    monitor?: MonitorStatus // MCR monitoring (absent from a node before 2026-10-10 until somebody watched)
    ptp?: PTPStatus // while an AES67 output runs
}
// MCR monitoring (node monitor.go): who watches which source, what it
// costs against its budget, and whether it is locked
export interface MonitorStatus {
    port: number // the gateway's UDP port
    running: boolean
    error?: string
    watched: string[] // sources with a monitor encode now ("input/cam1")
    cpu_cores: number // the monitor encodes' and senders' CPU together
    budget_cores: number // the most they may take (noded -monitor-max-cores); 0 from an older node
    max_viewers: number // the gateway's cap (noded -monitor-max-viewers)
    lock?: MonitorLock // set while monitoring is locked on the node
    sources: { source: string; viewers: number; silent_ms: number; video_bytes: number; upstream?: string }[]
    viewers: MonitorViewer[]
}
export interface MonitorViewer {
    id?: string // never in the node's status (MON-4)
    source: string
    viewer?: string
    since: string
    connected: boolean
    remote?: string
    relay: boolean
    rtt_ms: number
    pair: number
    audio_packets: number
}
export interface CentralStatus {
    url: string
    connected: boolean
    since: string
    error?: string
    node_id?: string
}
export interface Event {
    id: number
    ts: string
    kind: 'input' | 'encode' | 'destination' | 'output' | 'node' | 'machine' | 'storage' | 'config'
    entity: string
    type: string
    severity: 'info' | 'warning' | 'error'
    state?: string
    message: string
    actor?: string
}
export interface Sample {
    t: number
    bitrate_k?: number
    fps?: number
    dropped?: number
    cpu?: number
    mem?: number
    gpu?: number
    temp_c?: number
    gpu_temp_c?: number
    cores?: number[]
    clients?: number // pull destination: readers connected
    // an SRT link: round trip (ms), packets lost on the way and given up on, MPEG-TS errors (sums)
    rtt_ms?: number
    lost?: number
    link_dropped?: number
    ts_errors?: number
    udp_drops?: number // UDP datagrams dropped before they were read (sum)
    q?: number // encode: the video quantizer
    source_lost?: number // input: frames the source handed over that capture lost (sum)
    sound_late?: number // input: frames whose sound came after their picture (silence filled; sum)
    clock_late?: number // input: ticks capture's clock began more than a frame late (sum)
    // input: the audio history (audiohist.ts), per channel in dBFS with the
    // silent tail left out: the loudest sample, the quietest 100 ms, the
    // samples at full scale; per stereo pair the loudness (LUFS)
    peak?: number[]
    floor?: number[]
    clips?: number[]
    lufs?: number[]
}
// a pull destination's client (pullclients.go): until absent while connected
export interface PullClient {
    dest: string
    proto: 'srt' | 'rtmp'
    addr: string
    user?: string
    agent?: string
    since: string
    until?: string
    bytes: number
    loss_pct?: number
    srt?: SRTStats // SRT: the connection's figures (now; for a finished session, its last)
    tcp?: TCPStats // RTMP: its TCP connection, likewise
}

// an audio input's source (config.AudioSource)
export interface AudioSource {
    source: 'alsa' | 'aes67'
    channels?: number // a sound card's to open (0: 2)
    sdp?: string // an AES67 stream's description
}
// an AES67 stream heard on the network (aes67.Found)
export interface AES67Stream {
    name: string
    id: string
    group: string
    port: number
    source?: string
    encoding: string
    rate: number
    channels: number
    ptime?: string
    clock?: string
    sdp: string
    interface: string // the node's address on the network it was announced on
    seen: string
}
export type Kind = 'inputs' | 'encodes' | 'destinations' | 'templates' | 'groups' | 'outputs' | 'storage'
// what a change would interrupt that goes on running (the node's plan.go)
export interface Change {
    kind: 'input' | 'encode' | 'destination' | 'output'
    id: string
    name: string
    // restart; reconnect (a destination's receiver); caster (the caster cut
    // off, their page reconnects); return (the caster's return feed starts
    // over); comms (the caster's comms start over)
    action: 'restart' | 'reconnect' | 'caster' | 'return' | 'comms'
    why: string
}

// the health report (GET /api/machine/health-report): ready when nothing fails
export interface HealthReport {
    at: string
    ready: boolean
    checks: { area: string; name: string; state: 'ok' | 'warn' | 'fail' | 'skip'; detail?: string }[]
}
// the last or running self-test (GET/POST /api/machine/self-test)
export interface SelfTest {
    running: boolean
    started: string
    finished?: string
    passed: boolean // no step failed, none but the memory test cancelled
    forced?: boolean
    cancelled?: boolean // an operator cancelled it (while running: being cancelled)
    // state: waiting, running, pass, fail, skip, cancelled; a running memory
    // test's detail is how far memtester is ("Bit Flip (37 of 512), test 14 of 18, 6 min")
    steps: { name: string; state: string; detail?: string; value: number; unit?: string }[]
}

export class ApiError extends Error {
    status: number
    totp: '' | 'required' | 'wrong' // central's sign-in: a code from the authenticator app is needed, or was wrong
    constructor(status: number, message: string, totp = '') {
        super(message)
        this.status = status
        this.totp = totp === 'required' || totp === 'wrong' ? totp : ''
    }
}

// The same app serves a node directly (base "") and, from central, a node
// behind the reverse proxy (base "/nodes/{id}"): every node call is prefixed.
// Central's own API is addressed as "/central/api/…" and always hits the root.
// "/nodes/v<id>": a saved config opened in the editor, on a virtual node central runs
export const base: string = (location.pathname.match(/^\/nodes\/([0-9a-f]+|v\d+)(?=\/|$)/) ?? [''])[0]
export const nodeId: string = base.slice('/nodes/'.length)
export const virtualConfig: number | null = nodeId.startsWith('v') ? Number(nodeId.slice(1)) : null

// called on every 401 (a session that expired, anywhere in the app): the
// store goes back to its login instead of showing stale pages. Set by the
// store (this module must not import it).
let unauthorized = () => {}
export function onUnauthorized(f: () => void) {
    unauthorized = f
}

export const GET_TIMEOUT_MS = 30_000

// keepalive: the request outlives the page (a hang-up on pagehide)
async function call<T>(
    method: string,
    path: string,
    body?: unknown,
    ifMatch?: number,
    keepalive?: boolean,
): Promise<T> {
    const headers: Record<string, string> = {}
    if (body !== undefined) headers['Content-Type'] = 'application/json'
    if (ifMatch !== undefined) headers['If-Match'] = String(ifMatch)
    path = apiPath(path)
    let r: Response
    try {
        r = await fetch(path, {
            method,
            headers,
            body: body === undefined ? undefined : JSON.stringify(body),
            credentials: 'same-origin',
            keepalive,
            // a read that hangs (a stalled proxy, a wedged connection) fails
            // instead: one that never answered froze the fleet page and the
            // wallboard on their last values for good (WEB-29). Writes are
            // not cut (an apply pushes media for minutes).
            signal: method === 'GET' ? AbortSignal.timeout(GET_TIMEOUT_MS) : undefined,
        })
    } catch (e) {
        if (e instanceof DOMException && e.name === 'TimeoutError')
            throw new Error(`no answer in ${GET_TIMEOUT_MS / 1000} s`, { cause: e })
        throw e
    }
    if (r.status === 401) unauthorized()
    if (!r.ok) throw new ApiError(r.status, (await r.text()).trim() || r.statusText, r.headers.get('X-ESE-TOTP') ?? '')
    const text = await r.text()
    return (text ? JSON.parse(text) : undefined) as T
}

// apiPath is where a call goes: central's own API at the root, a node's
// through base (the proxy, or nothing on the node itself)
const apiPath = (path: string) =>
    path.startsWith('/central/api/') ? path.slice('/central'.length) : path.startsWith('/api/') ? base + path : path

// upload posts a form with a progress bar (XHR: fetch has no upload
// progress), addressed and failing like call(): a 401 goes to the login
// (WEB-41: the two uploads bypassed both)
export function upload<T>(path: string, fd: FormData, progress: (fraction: number) => void): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const x = new XMLHttpRequest()
        x.open('POST', apiPath(path))
        x.upload.onprogress = (e) => {
            if (e.lengthComputable) progress(e.loaded / e.total)
        }
        x.onload = () => {
            if (x.status === 401) unauthorized()
            if (x.status === 200) resolve(JSON.parse(x.responseText) as T)
            else reject(new ApiError(x.status, x.responseText.trim() || String(x.status)))
        }
        x.onerror = () => reject(new Error('upload failed'))
        x.send(fd)
    })
}

// nodes before 2026-09-25 send an empty list as null; the UI iterates them
export const normCfg = (c: Config): Config => ({
    ...c,
    inputs: c.inputs ?? [],
    encodes: c.encodes ?? [],
    destinations: c.destinations ?? [],
})
const cfgCall = (method: string, path: string, body?: unknown, ifMatch?: number) =>
    call<Config>(method, path, body, ifMatch).then(normCfg)

export const api = {
    auth: () => call<{ password_set: boolean; logged_in: boolean }>('GET', '/api/auth'),
    login: (password: string) => call<{ ok: boolean }>('POST', '/api/login', { password }),
    logout: () => call<{ ok: boolean }>('POST', '/api/logout'),
    config: () => cfgCall('GET', '/api/config'),
    status: () => call<Status>('GET', '/api/status'),
    events: (
        q: { since?: number; kind?: string; entity?: string; limit?: number; from?: number; to?: number } = {},
    ) => {
        const p = new URLSearchParams()
        for (const [k, v] of Object.entries(q)) if (v !== undefined) p.set(k, String(v))
        return call<Event[]>('GET', `/api/events?${p.toString()}`)
    },
    // from/to in unix seconds; the node picks one-second or one-minute samples
    series: (kind: string, id: string, from: number, to: number) =>
        call<Sample[]>('GET', `/api/series/${kind}/${id}?from=${from}&to=${to}`),
    // the last seconds up to the node's own now: a browser clock a moment
    // behind the node's left out the newest sample every other second
    seriesRecent: (kind: string, id: string, seconds: number) =>
        call<Sample[]>('GET', `/api/series/${kind}/${id}?seconds=${seconds}`),
    // central's encode templates: from central itself when the page is served
    // by central, else through the node (with its token; 404 when not enrolled)
    encodeTemplates: () => call<EncodeTemplate[]>('GET', base ? '/central/api/templates' : '/api/central/templates'),
    framePng: (id: string) => `${base}/api/inputs/${id}/frame.png`,
    // view "source": the input before slate/overlay/fallback; "output": what encodes get
    thumb: (id: string, tick: number, view: 'source' | 'output' = 'output') =>
        `${base}/api/inputs/${id}/thumb?t=${tick}${view === 'source' ? '&view=source' : ''}`,
    preview: (id: string, view: 'source' | 'output' = 'output') =>
        `${base}/api/inputs/${id}/preview.mjpeg?fps=5&w=480${view === 'source' ? '&view=source' : ''}`,
    // what an encode sends, a destination at its delay: decoded on the node
    // while asked for (a key frame every few seconds, or live)
    streamThumb: (kind: 'encodes' | 'destinations', id: string, tick: number) =>
        `${base}/api/${kind}/${id}/thumb?t=${tick}`,
    streamPreview: (kind: 'encodes' | 'destinations', id: string) => `${base}/api/${kind}/${id}/preview.mjpeg?w=480`,
    // an encode's newest key frame at full size (a passthrough's: its input's stream)
    encodeFramePng: (id: string) => `${base}/api/encodes/${id}/frame.png`,
    pullClients: (id: string, from: number, to: number) =>
        call<PullClient[]>('GET', `/api/destinations/${id}/clients?from=${from}&to=${to}`),
    mediaFile: (id: string) => `${base}/api/media/${id}/file`,
    stream: () => `${base}/api/stream`,
    put: (kind: Kind, id: string, body: unknown, version: number) =>
        cfgCall('PUT', `/api/${kind}/${id}`, body, version),
    // a list's order (feature reorder): the ids sent move among their places
    order: (kind: Kind, ids: string[], version: number) => cfgCall('PUT', `/api/order/${kind}`, ids, version),
    // query: with_destinations=1 (an encode's go too), force=1 (an input in use)
    del: (kind: Kind, id: string, version: number, query = '') =>
        cfgCall('DELETE', `/api/${kind}/${id}${query ? '?' + query : ''}`, undefined, version),
    putNode: (
        body: NodeIdentity & {
            max_buffer_seconds?: number
            full_buffers?: boolean
            ndi?: NDISettings
            ptp?: PTPSettings
            st2110?: ST2110
        },
        version: number,
    ) => cfgCall('PUT', '/api/node', body, version),
    // the plan of a write: checked as a save, applied not, answered with what
    // it would interrupt. A node from before plans ignores ?plan=1 and
    // applies: its answer is then the new document (store.planned)
    plan: (kind: Kind, id: string, body: unknown, version: number) =>
        call<Change[] | Config>('PUT', `/api/${kind}/${id}?plan=1`, body, version),
    planNode: (
        body: NodeIdentity & {
            max_buffer_seconds?: number
            full_buffers?: boolean
            ndi?: NDISettings
            ptp?: PTPSettings
            st2110?: ST2110
        },
        version: number,
    ) => call<Change[] | Config>('PUT', '/api/node?plan=1', body, version),
    media: () => call<Media[]>('GET', '/api/media'),
    // the NDI sources the node sees (503 with the reason when it cannot look, e.g. no NDI runtime)
    ndiSources: (refresh = false) =>
        call<{ name: string; url?: string }[]>('GET', '/api/ndi/sources' + (refresh ? '?refresh' : '')),
    // AES67 streams announced on the node's networks (Dante flows in AES67 mode too)
    aes67Streams: () => call<{ streams: AES67Stream[]; errors: string[] }>('GET', '/api/aes67/streams'),
    multiviewLayouts: () => call<{ name: string; tiles: Rect[] }[]>('GET', '/api/multiview/layouts'),
    // a reboot of the node: what it would stop, then asking it with the stops the dialog showed (kind:id)
    rebootPlan: () => call<RebootPlan>('GET', '/api/machine/reboot'),
    reboot: (stops: string[]) => call<RebootPlan>('POST', '/api/machine/reboot', { stops }),
    // a shutdown: the same plan and the same body; the node stays off until someone powers it on
    shutdown: (stops: string[]) => call<RebootPlan>('POST', '/api/machine/shutdown', { stops }),
    nvencProbe: () => call<{ ok: boolean }>('POST', '/api/machine/nvenc-probe'),
    // a machine problem acknowledged on the node, or no longer: each answers with the new document
    ackProblem: (key: string, note: string, version: number) =>
        cfgCall('POST', '/api/machine/acks', { key, note }, version),
    unackProblem: (key: string, version: number) =>
        cfgCall('DELETE', `/api/machine/acks?key=${encodeURIComponent(key)}`, undefined, version),
    // before the event: the health report (instant) and the self-test (a minute of load)
    healthReport: () => call<HealthReport>('GET', '/api/machine/health-report'),
    selfTest: () => call<SelfTest>('GET', '/api/machine/self-test'),
    startSelfTest: (force: boolean, memory = false) =>
        call<SelfTest>('POST', '/api/machine/self-test', { force, memory }),
    cancelSelfTest: () => call<SelfTest>('POST', '/api/machine/self-test/cancel'),
    casterControl: (inputId: string, c: CasterControl) =>
        call<void>('POST', `/api/inputs/${encodeURIComponent(inputId)}/caster/control`, c),
    // a player input: this player on air at their next key frame
    casterTake: (inputId: string, session: string) =>
        call<{ session: string; name?: string }>('POST', `/api/inputs/${encodeURIComponent(inputId)}/caster/take`, {
            session,
        }),
    // local caster links: the caster page on the node's own HTTPS port, no central
    castLinks: (inputId: string) =>
        call<CastLinks>('GET', `/api/inputs/${encodeURIComponent(inputId)}/caster/local-links`),
    makeCastLink: (inputId: string, b: { name: string; hours: number }) =>
        call<CastLink>('POST', `/api/inputs/${encodeURIComponent(inputId)}/caster/local-links`, b),
    revokeCastLink: (inputId: string, id: string) =>
        call<void>('DELETE', `/api/inputs/${encodeURIComponent(inputId)}/caster/local-links/${encodeURIComponent(id)}`),
    // an SCTE-35 ad break on an encode with scte35 on (node cues.go)
    cue: (encodeId: string, c: CueRequest) =>
        call<CueAnswer>('POST', `/api/encodes/${encodeURIComponent(encodeId)}/cue`, c),
    // an input's encodes and outputs to its backup (held there) or back to it
    failover: (inputId: string, use: 'backup' | 'main') =>
        call<void>('POST', `/api/inputs/${encodeURIComponent(inputId)}/failover`, { use }),
    // MCR monitoring on a node opened directly (no central: no TURN)
    monitorOffer: (b: MonitorOfferBody) => call<MonitorAnswer>('POST', '/api/monitor/offer', b),
    monitorHangUp: (session: string, keepalive?: boolean) =>
        call<void>('DELETE', `/api/monitor/session/${encodeURIComponent(session)}`, undefined, undefined, keepalive),
    // monitoring locked on the node (an editor's): whoever watches is cut; it
    // stays until unlocked. Both answer with the new document
    lockMonitoring: () => call<Config>('POST', '/api/monitoring/lock'),
    // the node's updates held (an editor's, before an event): until a time, or until lifted
    holdUpdates: (until: string | undefined, note: string) => call<Config>('POST', '/api/update/hold', { until, note }),
    liftUpdateHold: () => call<Config>('DELETE', '/api/update/hold'),
    unlockMonitoring: () => call<Config>('DELETE', '/api/monitoring/lock'),
    // the machine's own settings (time servers), not in the config document
    system: () => call<SystemStatus>('GET', '/api/system'),
    putSystem: (s: SystemSettings) => call<SystemStatus>('PUT', '/api/system', s),
    confirmSystem: () => call<SystemStatus>('POST', '/api/system/confirm'), // keep network settings on trial
    // node software: the updater's state, a version from central, or an
    // upload of the bundle and central's signed sums (a node offline)
    updateStatus: () => call<UpdateStatus>('GET', '/api/update'),
    startUpdate: (version: string) => call<UpdateStatus>('POST', '/api/update', { version }),
    releases: () => call<ReleasesState>('GET', base ? '/central/api/releases' : '/api/central/releases'),
    // XHR rather than fetch: a 100 MB bundle wants a progress bar
    uploadUpdate: (sums: File, bundle: File, progress: (fraction: number) => void) => {
        const fd = new FormData()
        fd.append('sums', sums)
        fd.append('bundle', bundle)
        return upload<UpdateStatus>('/api/update/upload', fd, progress)
    },
    fs: (path: string) => call<FsDir>('GET', '/api/fs?path=' + encodeURIComponent(path)),
    mkdir: (path: string) => call<{ ok: boolean }>('POST', '/api/fs/mkdir', { path }),
    storageRetry: () => call<{ ok: boolean }>('POST', '/api/storage/retry'),
    storageDevices: () => call<BlockDevice[]>('GET', '/api/storage/devices'),
    deleteMedia: (id: string) => call<{ ok: boolean }>('DELETE', `/api/media/${id}`),
    uploadMedia: async (file: File, name?: string) => {
        const fd = new FormData()
        fd.append('file', file)
        if (name) fd.append('name', name)
        const r = await fetch(base + '/api/media', { method: 'POST', body: fd, credentials: 'same-origin' })
        if (!r.ok) throw new ApiError(r.status, (await r.text()).trim() || r.statusText)
        return (await r.json()) as Media
    },
    ffmpegOptions: () => call<FFOption[]>('GET', '/api/ffmpeg/options'),
    // a sender's SDP fetched by the node (the sender sends no CORS headers),
    // and a 2110 output's own, as text
    fetchSDP: async (url: string) => {
        const r = await fetch(base + '/api/st2110/sdp', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ url }),
            credentials: 'same-origin',
        })
        if (!r.ok) throw new ApiError(r.status, (await r.text()).trim() || r.statusText)
        return r.text()
    },
    outputSDP: async (id: string, flow: string) => {
        const r = await fetch(base + `/api/outputs/${id}/sdp/${flow}`, { credentials: 'same-origin' })
        if (!r.ok) throw new ApiError(r.status, (await r.text()).trim() || r.statusText)
        return r.text()
    },
    groupStart: (id: string, version: number) => cfgCall('POST', `/api/groups/${id}/start`, undefined, version),
    groupStop: (id: string, version: number) => cfgCall('POST', `/api/groups/${id}/stop`, undefined, version),
}

// --- central (fleet) API: always at the site root, never behind a node base ---
export interface CentralUser {
    id: number
    email: string
    superadmin: boolean
    groups: number[]
    created_at: string
    totp: boolean // signs in with two-factor
    totp_enrol?: boolean // this session may only set two-factor up (a superadmin without it)
    sso_only: boolean // signs in by single sign-on only
    roles: Record<string, 'viewer' | 'editor'> // by group id (central roles.go)
    editor: boolean // may save templates, broadcasters, configs: a superadmin, an editor somewhere, or in no group
}
export interface CentralAuth {
    setup_required: boolean
    logged_in: boolean
    user: CentralUser | null
    sso: { enabled: boolean; label?: string } // single sign-on: the sign-in page's button (central sso.go)
}
// single sign-on's settings (superadmin): the client secret is write-only
export interface SSOSettings {
    enabled: boolean
    issuer: string
    client_id: string
    secret_set: boolean
    domains: string[]
    redirect_uri: string // give it to the OAuth client at the provider
    public_url_ok: boolean
}
export interface Alarm {
    kind: string
    entity: string
    state: string
    severity: string
    message: string
    ts: string
    acked: boolean
    acked_by?: string
    acked_until?: string
    ack_note?: string
}
// the audit log (central audit.go): central's changes and sign-ins, and the
// nodes' changes (their events with an actor)
export interface AuditEntry {
    ts: string
    actor: string
    token?: boolean // by an API token
    ip?: string // central's rows
    source: string // central or node
    action: string // "Delete user"; a node's: its event's kind and type ("config changed")
    route?: string
    node_id?: string
    node_name?: string // empty once the node is removed
    target?: string // a name or the path's ids; a node's: the entity
    message?: string // a node's: what changed
}
// a node's config history (node history.go, DECISIONS 2026-10-20 "config
// history and revert"): the documents its last 20 accepted writes
// replaced, each with the change that replaced it
export interface HistoryEntry {
    version: number
    time: string // when the change that replaced it was made
    actor: string // who made that change
    summary: string // what it changed
    size: number // the kept file's bytes
}
export interface History {
    current: number // the version now: a revert's if_match
    entries: HistoryEntry[] // newest first
}
export interface AuditPage {
    entries: AuditEntry[]
    actors: string[] // everyone in the window, for the filter
    more: boolean // older ones in the window than the limit gave
}
export interface CentralMedia extends Media {
    pushed: Record<string, string>
}
export interface PushResult {
    node_id: string
    name: string
    node_media_id?: string
    error?: string
}
export interface NodeInfo {
    id: string
    name: string
    hostname: string
    version: string
    features: string[] // what the node can, from its hello; [] from a node before the list
    unsupported: boolean // it runs a version below the floor (internal/compat/oldest-node): proxied, nothing promised
    public_ip: string
    private_ips: string[]
    interfaces: { name: string; mac: string }[] // the machine's network interfaces, from its hello
    cloud: CloudInfo | null // the cloud the node runs in, from its hello; null on bare metal
    port: number
    self_public_ip: string
    country: string
    city: string
    online: boolean
    connected_at: string | null
    config_version: number // of the mirrored config: refetched only when it moved
    last_seen: string | null
    groups: number[]
    role?: 'viewer' | 'editor' // the user's: an editor in any of its groups edits it (central roles.go)
    created_at: string
    alarms: Alarm[]
    // its timing as its heartbeat last said; null from a node that does not report it
    sync: {
        clock: 'synced' | 'unsynced' | 'host' // host: a container or WSL keeps the clock
        offset_ms?: number | null
        reference?: 'locked' | 'unlocked' // its DeckLink reference input; absent: none
        reference_mode?: string
    } | null
    monitor_lock?: MonitorLock // set while monitoring is locked on it (its mirrored config)
    update_hold?: UpdateHold // set while its updates are held (its mirrored config)
    pair?: NodePair | null // its 1+1 pair (pairs.go)
    rebooting_until?: string | null // a reboot was asked: offline inside this window is "rebooting", not an alarm yet
    shutdown_by?: string // who shut it down from the interface; set until it reconnects. Counts only when last_seen is within 10 minutes of shutdown_at (shutDownBy)
    shutdown_at?: string | null
}
// a pair's PTP-numbered input, its frames' phase on each node (a signed
// fraction of a frame): over half a frame apart, numbered one apart
export interface PairFrames {
    input_id: string
    a_phase: number
    b_phase: number
    apart: boolean
}

// 1+1 pairs (central pairs.go, config/pair.go): two nodes fed the same
// signal, both encoding and sending; in step while their inputs, encodes,
// destinations, templates and groups are the same (each node's hardware
// aside), by the configs they sent; null while either has sent none
export interface PairStep {
    in_step: boolean | null
    differences: string[] // what differs, when out of step
    copy_error: string // why the last change on one did not reach the other ('' when it did); until one does or a Resync
}
export interface NodePair extends PairStep {
    id: number
    name: string
    side: 'a' | 'b'
    partner: string // the other node's id
}
export interface PairNode {
    id: string
    name: string
    online: boolean
}
export interface Pair extends PairStep {
    id: number
    name: string
    a: PairNode
    b: PairNode
}
// cloud nodes (superadmin): accounts central creates node VMs in, and the VMs
export interface CloudAccount {
    id: number
    provider: string // gcp or aws
    name: string
    project: string // GCP's project id; AWS's account id
    region: string // the create dialog's default
    key_email: string // the key's service account, "" = central's own (GCP); the IAM user's ARN (AWS)
    created_at: string
}
export const cloudName = (provider: string) => (provider === 'aws' ? 'AWS' : 'Google Cloud')
export interface CloudPort {
    protocol: string // tcp or udp
    from: number
    to: number // = from for one port
    note?: string
}
// what a VM costs and has cost at list prices: an estimate, not the bill
export interface CloudCost {
    currency: string
    hourly: number // running: shape, disk, address
    stopped_hourly: number // stopped: disk, and the address when static
    egress_gb_rate: number
    run_hours: number // this run, since the last start
    run_egress_gb: number
    run_cost: number
    total_run_hours: number // since the VM was created
    total_stopped_hours: number
    total_egress_gb: number
    total_cost: number
    price_source: string // catalog, partial or builtin
}
export interface CloudVM {
    id: number
    account_id: number
    provider: string
    name: string // the VM's name in the cloud
    node_name: string
    region: string
    zone: string
    shape: string
    gpus?: number // the shape's GPUs: a stopped GPU VM may not start again at once (CLD-12)
    disk_gb: number
    ports: CloudPort[]
    sources: string[]
    // provisioning, creating, running, stopping, stopped, deleting, missing, failed
    state: string
    public_ip: string
    static_ip: boolean // reserved: the address survives stops
    instance_id: string
    error: string
    enrollment_id: number | null
    node_id: string | null
    created_at: string
    updated_at: string
    running_since: string | null
    install_step: string // until its node enrolls: the install's latest step
    cost: CloudCost
}
export interface CloudFit {
    provider: string
    name: string // the machine type
    vcpu: number
    mem_gb: number
    gpu?: string
    gpus?: number
    nvenc: number // 1080p60 NVENC encodes it takes
    x264: number // 1080p60 x264 encodes it takes
    note?: string
    fits: boolean
    why?: string // why it does not fit
    suggested: boolean // the cheapest that fits
    hourly: number // the shape alone; 0 = unknown
}
export interface CloudPrices {
    provider: string
    region: string
    currency: string
    source: string // catalog, partial or builtin (approximate)
    as_of: string
    shapes: Record<string, number>
    disk_gb_month: number
    ip_hour: number
    ip_idle_hour: number
    egress_gb: number
    egress_tiers?: { from_gb: number; per_gb: number }[] // the rate from each tier's first GB (CLD-18)
}
export interface CloudRegion {
    name: string // europe-west1
    location: string // Belgium
    shapes: string[] // catalogue shapes it offers
}
export interface CloudRegions {
    regions: CloudRegion[]
    source: string // account (read from the cloud) or builtin
    error?: string
}
export interface CloudShapes {
    prices: CloudPrices
    price_error?: string
    fits: CloudFit[]
}
export interface CloudNeed {
    encodes: number
    gpu: boolean
    codec: string
    height: number
}
export interface CloudVMBody {
    account_id: number
    name: string
    groups: number[]
    region: string
    zone?: string
    shape: string
    disk_gb: number
    ports: CloudPort[] | null // null: the default set
    sources: string[]
    static_ip: boolean
    vm_name: string // "": from the node's name
}
// cloud VM management: the switch and the Google sign-in's OAuth client
export interface CloudSettings {
    enabled: boolean
    gcp_client_id: string
    gcp_client_secret_set: boolean
    redirect_uri: string // to give the OAuth client in Google's console
    public_url_ok: boolean
    vms: number
    aws_policy: string // the IAM policy an AWS account's user needs, to copy
}
export interface OnboardStep {
    name: string
    state: string // running, done, warn, failed
    detail?: string
    link?: string
}
export interface OnboardingView {
    signed_in: boolean
    email: string
    error: string
    running: boolean
    steps: OnboardStep[]
    account_id: number
}
export interface GcpProject {
    id: string
    name: string
    number: string
}
export interface GcpChoices {
    email: string
    projects: GcpProject[]
    billing_accounts: { name: string; display_name: string; open: boolean }[]
    organizations: { name: string; display_name: string }[]
    warnings: string[]
}
export const cloudDefaultPorts = (): CloudPort[] => [
    { protocol: 'tcp', from: 1935, to: 1935, note: 'RTMP pull destinations, RTMP listener inputs' },
    { protocol: 'udp', from: 8890, to: 8890, note: 'SRT pull destinations' },
    { protocol: 'tcp', from: 8888, to: 8888, note: 'HLS origins (hls_pull destinations)' },
    { protocol: 'udp', from: 9000, to: 9019, note: 'SRT listener inputs' },
    { protocol: 'udp', from: 8300, to: 8300, note: "MCR monitoring (WebRTC; central's TURN when closed)" },
]
export const portText = (p: CloudPort) => `${p.from}${p.to !== p.from ? `-${p.to}` : ''}/${p.protocol}`
// money: "$1.24"; under a cent per hour shows tenths of a cent
export const money = (v: number, currency = 'USD') =>
    new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'USD',
        maximumFractionDigits: v !== 0 && Math.abs(v) < 0.1 ? 3 : 2,
    }).format(v)
export interface SSHKeyInfo {
    public_key: string // the authorized_keys line every node gets
    fingerprint: string // SHA256:…, as ssh shows it
    created_at: string
    taken: { kind: 'download' | 'rotate'; user: string; from: string; at: string }[] // the latest (SEC-15)
}
export interface SigningKeyInfo {
    fingerprint: string // what nodes report: sha256 of the key, 16 hex
    created_at: string
    next_fingerprint?: string // a rotation under way
    next_since?: string
    nodes: number
    nodes_next: number // holding the next key
    nodes_unknown: number // reporting no keys (older software, or not online since)
    nodes_offline: number
    missing?: string[] // nodes without the next key
}
export interface Enrollment {
    id: number
    name: string
    groups: number[]
    created_at: string
    expires_at: string
    used_at: string | null
    node_id: string | null
    token?: string
}
const root = <T>(method: string, path: string, body?: unknown, keepalive?: boolean) =>
    call<T>(method, '/central' + path, body, undefined, keepalive)
// a monitor wall (central walls.go): a preset layout's tiles, each a node's
// source watched live; a tile on a node this user cannot see is hidden
export interface WallTile {
    node?: string
    source?: string // "input/<id>", "encode/<id>", "destination/<id>"
    hidden?: boolean
    // a far-end return: a platform's own player, what its viewers see
    platform?: Platform
    channel?: string // twitch, kick: the channel's name; youtube: a channel id (UC…) or a video id; web: an https link
}
export interface Wall {
    id: number
    name: string
    layout: string
    tiles: WallTile[]
    updated_by: string
    updated_at: string
}
// a viewer's offer to watch a source (MCR monitoring): "input/<id>", the
// browser's SDP, the pair to hear first (0: channels 1+2, -1 none)
export interface MonitorOfferBody {
    source: string
    sdp: string
    pair: number
    location?: number // the viewer's MCR location: through its relay
}
export interface MonitorAnswer {
    sdp: string
    session: string
    via?: string // the relay the session is on
    via_name?: string
    fallback?: string // why the location's relay was not used
}
// an MCR location (central locations.go): where operators watch from, and
// the relay that serves them there
export interface MonitorLocation {
    id: number
    name: string
    relay_node?: string
    relay_name?: string
    relay_vm?: number
    relay: 'online' | 'offline' | 'installing' | 'none'
    updated_by: string
    updated_at: string
}
export interface NodeGroup {
    id: number
    name: string
}
// The broadcaster directory (DECISIONS 2026-10-06 "broadcaster directory");
// keys and passwords are plain fields, masked in the form until shown
export interface Broadcaster {
    id: number
    name: string
    notes?: string
    contacts: BroadcasterContact[]
    channels: { platform: Platform; channel: string }[]
    feeds: BroadcasterFeed[]
    updated_by: string
    updated_at: string
}
export interface BroadcasterContact {
    name: string
    role?: string
    email?: string
    phone?: string
    notes?: string
}
export type DeliveryKind = 'rtmp_push' | 'srt_caller' | 'rtmp_pull' | 'srt_listener' | 'udp' | 'other'
export interface BroadcasterFeed {
    id: string // given by central; '' for a new one
    description: string // free text: the feed, the game, the event
    notes?: string
    delivery: {
        kind: DeliveryKind
        url?: string // rtmp_push: the server (the key apart); srt_caller: srt://; udp: udp:// or rtp://
        key?: string // rtmp_push's stream key
        srt?: { latency_ms?: number; pbkeylen?: number; streamid?: string; passphrase?: string }
        stream?: string // rtmp_pull, srt_listener: what we gave them
        user?: string
        pass?: string
        max_clients?: number
    }
}
// a destination made from one of a broadcaster's feeds, on a node the user sees
export interface BroadcasterDest {
    node: string
    node_name: string
    online: boolean
    destination: Destination
    encode_name: string
    state: string // its latest state ('' before any)
    message?: string
    since?: string
    differs: string[] // where it no longer matches its feed (passwords are not compared)
    pull_urls?: string[] // a pull: where they pull it from, on each address of the node
}
export const central = {
    auth: () => root<CentralAuth>('GET', '/api/auth'),
    // token: the setup token from central's log (SEC-14)
    setup: (email: string, password: string, token = '') =>
        root<{ ok: boolean; user: CentralUser }>('POST', '/api/setup', { email, password, token }),
    // totp: the authenticator app's code or a recovery code, when the account has two-factor
    login: (email: string, password: string, totp = '') =>
        root<{ ok: boolean; user: CentralUser }>('POST', '/api/login', { email, password, totp: totp || undefined }),
    logout: () => root<{ ok: boolean }>('POST', '/api/logout'),
    nodes: () => root<NodeInfo[]>('GET', '/api/nodes'),
    // the fleet's order, one for everyone: the nodes sent move among their places
    fleetOrder: (ids: string[]) => root<void>('PUT', '/api/fleet/order', ids),
    // a node's config history and a revert of one of its kept documents, from
    // the Changes drawer: the node's own routes through the proxy, whichever
    // page this is (history.ts)
    configHistory: (nodeId: string) => call<History>('GET', `/nodes/${nodeId}/api/config/history`),
    revertPlan: (nodeId: string, version: number, ifMatch: number) =>
        call<Change[]>('POST', `/nodes/${nodeId}/api/config/revert?plan=1`, { version, if_match: ifMatch }),
    revert: (nodeId: string, version: number, ifMatch: number) =>
        call<Config>('POST', `/nodes/${nodeId}/api/config/revert`, { version, if_match: ifMatch }),
    audit: (q: { from?: string; to?: string; actor?: string; node?: string; limit?: number }) =>
        root<AuditPage>(
            'GET',
            '/api/audit?' +
                new URLSearchParams(
                    Object.entries(q)
                        .filter(([, v]) => v !== undefined && v !== '')
                        .map(([k, v]) => [k, String(v)]),
                ).toString(),
        ),
    casterInvites: (nodeId: string) => root<CasterInvite[]>('GET', `/api/nodes/${nodeId}/casters/invites`),
    createCasterInvite: (nodeId: string, b: { input_id: string; name: string; hours: number }) =>
        root<CasterInvite>('POST', `/api/nodes/${nodeId}/casters/invites`, b),
    revokeCasterInvite: (nodeId: string, id: number) =>
        root<void>('DELETE', `/api/nodes/${nodeId}/casters/invites/${id}`),
    // MCR monitoring through central: it adds TURN credentials for the node
    walls: () => root<Wall[]>('GET', '/api/walls'),
    wallLayouts: () => root<{ name: string; tiles: Rect[] }[]>('GET', '/api/walls/layouts'),
    // updated_at: the wall as loaded; a save over a newer one is a 409
    saveWall: (w: Pick<Wall, 'name' | 'layout' | 'tiles'> & { id?: number; updated_at?: string }) =>
        w.id ? root<Wall>('PUT', `/api/walls/${w.id}`, w) : root<Wall>('POST', '/api/walls', w),
    deleteWall: (id: number) => root<void>('DELETE', `/api/walls/${id}`),
    broadcasters: () => root<Broadcaster[]>('GET', '/api/broadcasters'),
    broadcaster: (id: number) => root<Broadcaster>('GET', `/api/broadcasters/${id}`),
    saveBroadcaster: (b: Omit<Broadcaster, 'id' | 'updated_by' | 'updated_at'> & { id?: number }) =>
        b.id
            ? root<Broadcaster>('PUT', `/api/broadcasters/${b.id}`, b)
            : root<Broadcaster>('POST', '/api/broadcasters', b),
    deleteBroadcaster: (id: number) => root<void>('DELETE', `/api/broadcasters/${id}`),
    broadcasterDests: (id: number) => root<BroadcasterDest[]>('GET', `/api/broadcasters/${id}/destinations`),
    monitorOffer: (nodeId: string, b: MonitorOfferBody) =>
        root<MonitorAnswer>('POST', `/api/nodes/${nodeId}/monitor/offer`, b),
    monitorHangUp: (nodeId: string, session: string, via?: string, keepalive?: boolean) =>
        root<void>(
            'DELETE',
            `/api/nodes/${nodeId}/monitor/session/${encodeURIComponent(session)}${via ? `?via=${encodeURIComponent(via)}` : ''}`,
            undefined,
            keepalive,
        ),
    locations: () => root<MonitorLocation[]>('GET', '/api/locations'),
    saveLocation: (l: { id?: number; name: string; relay_node?: string; relay_vm?: number }) =>
        l.id
            ? root<MonitorLocation>('PUT', `/api/locations/${l.id}`, l)
            : root<MonitorLocation>('POST', '/api/locations', l),
    deleteLocation: (id: number) => root<void>('DELETE', `/api/locations/${id}`),
    // central's TURN: the check, and opening its ports on Google Cloud (superadmin)
    turn: () => root<TURNInfo>('GET', '/api/turn'),
    turnConnect: (back: string) => root<{ url: string }>('POST', '/api/turn/connect', { back }),
    turnFirewall: () => root<{ rule: string; changed: boolean }>('POST', '/api/turn/firewall'),
    // initial: 1 adds each entity's last state from before `from`, so a timeline knows how it starts
    nodeEvents: (
        id: string,
        q: {
            from?: number
            to?: number
            kind?: string
            entity?: string
            limit?: number
            initial?: 1
            since?: number
        } = {},
    ) => {
        const p = new URLSearchParams()
        for (const [k, v] of Object.entries(q)) if (v !== undefined) p.set(k, String(v))
        return root<Event[]>('GET', `/api/nodes/${id}/events?${p.toString()}`)
    },
    enrollments: () => root<Enrollment[]>('GET', '/api/enrollments'),
    createEnrollment: (name: string, groups: number[] = []) =>
        root<Enrollment>('POST', '/api/enrollments', { name, groups }),
    // a new token (24 h) for an enrollment its node has not used yet; the old one stops working
    newEnrollmentToken: (id: number) => root<Enrollment>('POST', `/api/enrollments/${id}/token`),
    deleteEnrollment: (id: number) => root<{ ok: boolean }>('DELETE', `/api/enrollments/${id}`),
    // the fleet SSH key (superadmin): its public half and fingerprint; rotate makes a new one
    sshKey: () => root<SSHKeyInfo>('GET', '/api/ssh-key'),
    // the fleet key: the password again, and on record (SEC-15)
    rotateSSHKey: (password: string) => root<SSHKeyInfo>('POST', '/api/ssh-key/rotate', { password }),
    sshPrivateKey: async (password: string): Promise<Blob> => {
        const r = await fetch('/api/ssh-key/private', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ password }),
            credentials: 'same-origin',
        })
        if (!r.ok) throw new ApiError(r.status, (await r.text()).trim() || r.statusText)
        return r.blob()
    },
    // the user's own account (SEC-17)
    changeMyPassword: (old: string, next: string) =>
        root<{ ok: boolean; sessions_ended: number }>('PUT', '/api/me/password', { old, new: next }),
    endMyOtherSessions: () => root<{ ok: boolean; sessions_ended: number }>('DELETE', '/api/me/sessions'),
    // two-factor sign-in (central totp.go): a new secret, confirmed by a code; off; another's reset
    totpSetup: (password: string) => root<{ secret: string; uri: string }>('POST', '/api/me/totp/setup', { password }),
    totpEnable: (code: string) =>
        root<{ ok: boolean; recovery_codes: string[] }>('POST', '/api/me/totp/enable', { code }),
    totpDisable: (password: string, code: string) =>
        root<{ ok: boolean }>('DELETE', '/api/me/totp', { password, code }),
    resetUserTOTP: (id: number) => root<{ ok: boolean }>('DELETE', `/api/users/${id}/totp`),
    ssoSettings: () => root<SSOSettings>('GET', '/api/sso'),
    putSSOSettings: (
        s: Partial<Omit<SSOSettings, 'secret_set' | 'redirect_uri' | 'public_url_ok'>> & { client_secret?: string },
    ) => root<SSOSettings>('PUT', '/api/sso', s),
    // a node's token replaced: locked out until it enrolls with the token this gives (SEC-25)
    replaceNodeToken: (id: string) => root<Enrollment>('POST', `/api/nodes/${id}/token`),
    signingKey: () => root<SigningKeyInfo>('GET', '/api/signing-key'),
    startSigningRotation: () => root<SigningKeyInfo>('POST', '/api/signing-key/rotate'),
    finishSigningRotation: () => root<SigningKeyInfo>('POST', '/api/signing-key/rotate/finish'),
    cancelSigningRotation: () => root<SigningKeyInfo>('DELETE', '/api/signing-key/rotate'),
    // the node and everything central keeps of it; it cannot reconnect
    deleteNode: (id: string) => root<{ ok: boolean }>('DELETE', `/api/nodes/${id}`),
    ack: (nodeId: string, kind: string, entity: string, hours = 0, note = '') =>
        root<Alarm[]>('POST', `/api/nodes/${nodeId}/alarms/ack`, { kind, entity, hours, note }),
    unack: (nodeId: string, kind: string, entity: string) =>
        root<Alarm[]>('DELETE', `/api/nodes/${nodeId}/alarms/ack?${new URLSearchParams({ kind, entity }).toString()}`),
    pushMedia: (id: string) => root<PushResult[]>('POST', `/api/media/${id}/push`),
    // what the node software central serves (/install/manifest, one
    // "name value" line each: version, ffmpeg, ...); {} when it serves none
    manifest: async (): Promise<Record<string, string>> => {
        const res = await fetch('/install/manifest')
        if (!res.ok) return {}
        return Object.fromEntries(
            (await res.text())
                .split('\n')
                .filter(Boolean)
                .map((l): [string, string] => [l.split(' ')[0] ?? '', l.slice(l.indexOf(' ') + 1)]),
        )
    },
    nodeConfig: (id: string) => cfgCall('GET', `/central/api/nodes/${id}/config`),
    groups: () => call<NodeGroup[]>('GET', '/central/api/groups'),
    createGroup: (name: string) => call<NodeGroup>('POST', '/central/api/groups', { name }),
    updateGroup: (id: number, name: string) => call<NodeGroup>('PUT', `/central/api/groups/${id}`, { name }),
    deleteGroup: (id: number) => call<{ ok: boolean }>('DELETE', `/central/api/groups/${id}`),
    pairs: () => call<Pair[]>('GET', '/central/api/pairs'),
    createPair: (name: string, a: string, b: string) =>
        call<{ id: number }>('POST', '/central/api/pairs', { name, a, b }),
    deletePair: (id: number) => call<void>('DELETE', `/central/api/pairs/${id}`),
    // to: the node made like the other in the pair's entities (its own hardware kept)
    resyncPairPlan: (id: number, to: 'a' | 'b') =>
        call<Change[]>('POST', `/central/api/pairs/${id}/resync/plan`, { to }),
    resyncPair: (id: number, to: 'a' | 'b') => call<{ ok: boolean }>('POST', `/central/api/pairs/${id}/resync`, { to }),
    // the pair's PTP-numbered inputs compared: apart, numbered one frame apart
    pairFrames: (id: number) => call<PairFrames[]>('GET', `/central/api/pairs/${id}/frames`),
    updateNode: (id: string, body: { name?: string; groups?: number[] }) =>
        call<NodeInfo>('PUT', `/central/api/nodes/${id}`, body),
    // users (superadmin): a user sees the nodes of their groups; password "" on update = unchanged
    users: () => call<CentralUser[]>('GET', '/central/api/users'),
    createUser: (body: UserBody) => call<CentralUser>('POST', '/central/api/users', body),
    updateUser: (id: number, body: UserBody) => call<CentralUser>('PUT', `/central/api/users/${id}`, body),
    deleteUser: (id: number) => call<{ ok: boolean }>('DELETE', `/central/api/users/${id}`),
    // API tokens: the signed-in user's own; the secret is only in the create response
    tokens: () => call<ApiToken[]>('GET', '/central/api/tokens'),
    createToken: (name: string, expiresDays: number, scope: 'full' | 'monitoring' | 'companion' = 'full') =>
        call<ApiToken>('POST', '/central/api/tokens', { name, expires_days: expiresDays, scope }),
    deleteToken: (id: number) => call<{ ok: boolean }>('DELETE', `/central/api/tokens/${id}`),
    // alerting (superadmins): every write answers the list it changed
    alertChannels: () => call<AlertChannel[]>('GET', '/central/api/alerts/channels'),
    saveAlertChannel: (ch: AlertChannelBody) =>
        ch.id
            ? call<AlertChannel[]>('PUT', `/central/api/alerts/channels/${ch.id}`, ch)
            : call<AlertChannel[]>('POST', '/central/api/alerts/channels', ch),
    deleteAlertChannel: (id: number) => call<AlertChannel[]>('DELETE', `/central/api/alerts/channels/${id}`),
    testAlertChannel: (id: number) => call<{ ok: boolean }>('POST', `/central/api/alerts/channels/${id}/test`),
    alertRules: () => call<AlertRule[]>('GET', '/central/api/alerts/rules'),
    saveAlertRule: (r: AlertRuleBody) =>
        r.id
            ? call<AlertRule[]>('PUT', `/central/api/alerts/rules/${r.id}`, r)
            : call<AlertRule[]>('POST', '/central/api/alerts/rules', r),
    deleteAlertRule: (id: number) => call<AlertRule[]>('DELETE', `/central/api/alerts/rules/${id}`),
    maintenance: () => call<MaintenanceWindow[]>('GET', '/central/api/alerts/maintenance'),
    createMaintenance: (w: { match: AlertMatch; starts_at?: string; ends_at: string; note: string }) =>
        call<MaintenanceWindow[]>('POST', '/central/api/alerts/maintenance', w),
    deleteMaintenance: (id: number) => call<MaintenanceWindow[]>('DELETE', `/central/api/alerts/maintenance/${id}`),
    incidents: () => call<AlertIncident[]>('GET', '/central/api/alerts/incidents'),
    // cloud nodes (superadmin)
    cloudSettings: () => root<CloudSettings>('GET', '/api/cloud/settings'),
    putCloudSettings: (body: {
        enabled?: boolean
        gcp_client_id?: string
        gcp_client_secret?: string
        forget_secret?: boolean
    }) => root<CloudSettings>('PUT', '/api/cloud/settings', body),
    gcpConnect: () => root<{ url: string }>('POST', '/api/cloud/gcp/connect'),
    gcpOnboarding: () => root<OnboardingView>('GET', '/api/cloud/gcp/onboarding'),
    gcpForget: () => root<{ ok: boolean }>('DELETE', '/api/cloud/gcp/onboarding'),
    gcpChoices: () => root<GcpChoices>('GET', '/api/cloud/gcp/choices'),
    gcpSetup: (body: {
        project: string
        create: boolean
        parent: string
        billing_account: string
        region: string
        name: string
    }) => root<OnboardingView>('POST', '/api/cloud/gcp/setup', body),
    makeCloudIPStatic: (id: number) => root<CloudVM>('POST', `/api/cloud/vms/${id}/static`),
    cloudAccounts: () => root<CloudAccount[]>('GET', '/api/cloud/accounts'),
    createCloudAccount: (body: { provider: string; name: string; project: string; region: string; key: string }) =>
        root<CloudAccount>('POST', '/api/cloud/accounts', body),
    // key_id and key_url: the account's key, still valid in Google or IAM (CLD-8)
    deleteCloudAccount: (id: number) =>
        root<{ ok: boolean; key_id?: string; key_url?: string }>('DELETE', `/api/cloud/accounts/${id}`),
    // an account's regions, or without one the provider's built-in list
    cloudRegions: (account?: number, provider = 'gcp') =>
        root<CloudRegions>('GET', `/api/cloud/regions?${account ? `account=${account}` : `provider=${provider}`}`),
    cloudShapes: (account: number, region: string, need: CloudNeed) =>
        root<CloudShapes>(
            'GET',
            `/api/cloud/shapes?${new URLSearchParams({
                account: String(account),
                region,
                encodes: String(need.encodes),
                gpu: String(need.gpu),
                codec: need.codec,
                height: String(need.height),
            }).toString()}`,
        ),
    // every VM, or the one a node runs on
    cloudVMs: (node?: string) =>
        root<CloudVM[]>('GET', `/api/cloud/vms${node ? `?node=${encodeURIComponent(node)}` : ''}`),
    createCloudVM: (body: CloudVMBody) => root<CloudVM>('POST', '/api/cloud/vms', body),
    stopCloudVM: (id: number) => root<CloudVM>('POST', `/api/cloud/vms/${id}/stop`),
    startCloudVM: (id: number) => root<CloudVM>('POST', `/api/cloud/vms/${id}/start`),
    deleteCloudVM: (id: number) => root<{ ok: boolean }>('DELETE', `/api/cloud/vms/${id}`),
    setCloudPorts: (id: number, ports: CloudPort[], sources: string[]) =>
        root<CloudVM>('PUT', `/api/cloud/vms/${id}/ports`, { ports, sources }),
    // node software vendor files (superadmin): Blackmagic uploads and the NVIDIA pin
    vendor: () => call<VendorState>('GET', '/central/api/vendor'),
    deleteVendor: (name: string) => call<VendorState>('DELETE', `/central/api/vendor/${encodeURIComponent(name)}`),
    setNvidia: (branch: string) => call<VendorState>('PUT', '/central/api/vendor/nvidia', { branch }),
    // XHR rather than fetch: a 260 MB .deb wants a progress bar
    uploadVendor: (file: File, progress: (fraction: number) => void) => {
        const fd = new FormData()
        fd.append('file', file)
        return upload<VendorState>('/central/api/vendor', fd, progress)
    },
    // ffmpeg builds (superadmin): every build is kept; nodes get the default
    ffmpeg: () => call<FFmpegState>('GET', '/central/api/ffmpeg'),
    buildFFmpeg: (version: string, decklink: boolean, makeDefault: boolean) =>
        call<FFmpegState>('POST', '/central/api/ffmpeg/builds', { version, decklink, make_default: makeDefault }),
    rebuildFFmpeg: () => call<FFmpegState>('POST', '/central/api/ffmpeg/rebuild'),
    setDefaultFFmpeg: (id: string) => call<FFmpegState>('PUT', '/central/api/ffmpeg/default', { id }),
    abortFFmpeg: (id: string) => call<FFmpegState>('POST', `/central/api/ffmpeg/builds/${id}/abort`),
    deleteFFmpeg: (id: string) => call<FFmpegState>('DELETE', `/central/api/ffmpeg/builds/${id}`),
    // node software versions central keeps (superadmin); a node installs one
    // through its own /api/update, reached through central's proxy
    // saved node configs (superadmin): kept on central, checked against and deployed to nodes
    configs: () => call<SavedConfig[]>('GET', '/central/api/configs'),
    config: (id: number) => call<SavedConfig>('GET', `/central/api/configs/${id}`),
    saveConfig: (body: { name: string; note?: string; from_node?: string; doc?: unknown }) =>
        call<SavedConfig>('POST', '/central/api/configs', body),
    updateConfig: (id: number, body: { name: string; note?: string; doc?: unknown }) =>
        call<SavedConfig>('PUT', `/central/api/configs/${id}`, body),
    deleteConfig: (id: number) => call<{ ok: boolean }>('DELETE', `/central/api/configs/${id}`),
    // encode templates (any user); a node's editor picks them (api.encodeTemplates)
    templates: () => call<EncodeTemplate[]>('GET', '/central/api/templates'),
    saveTemplate: (t: Omit<TemplateBody, 'id'>) => call<EncodeTemplate>('POST', '/central/api/templates', t),
    updateTemplate: (t: TemplateBody) => call<EncodeTemplate>('PUT', `/central/api/templates/${t.id}`, t),
    deleteTemplate: (id: number) => call<{ ok: boolean }>('DELETE', `/central/api/templates/${id}`),
    // resolutions: node -> conflict id -> "use:<value>" | "ignore"
    checkConfig: (id: number, nodes: string[], resolutions: Record<string, Record<string, string>> = {}) =>
        call<NodeCheck[]>('POST', `/central/api/configs/${id}/check`, { nodes, resolutions }),
    applyConfig: (id: number, nodes: string[], resolutions: Record<string, Record<string, string>> = {}) =>
        call<NodeCheck[]>('POST', `/central/api/configs/${id}/apply`, { nodes, resolutions }),
    hardwarePresets: () => call<{ name: string; hardware: Hardware }[]>('GET', '/central/api/hardware-presets'),
    setHardware: (id: number, hw: Hardware) => call<Hardware>('PUT', `/central/api/configs/${id}/hardware`, hw),
    releases: () => call<ReleasesState>('GET', '/central/api/releases'),
    // a freeze on central's deploys: public to read, a superadmin's to set (at most 7 days ahead) and lift
    deployFreeze: () => call<DeployFreeze>('GET', '/central/api/deploy-freeze'),
    setDeployFreeze: (until: string, note: string) =>
        call<DeployFreeze>('PUT', '/central/api/deploy-freeze', { until, note }),
    liftDeployFreeze: () => call<DeployFreeze>('DELETE', '/central/api/deploy-freeze'),
    // each feature's first archived version (any user): "needs a newer node version"
    features: () => call<{ first: Record<string, string> }>('GET', '/central/api/features'),
    deleteRelease: (v: string) => call<ReleasesState>('DELETE', `/central/api/releases/${encodeURIComponent(v)}`),
    nodeUpdateStatus: (nodeId: string) => call<UpdateStatus>('GET', `/nodes/${nodeId}/api/update`),
    // a player's screen shows another input: through the proxy, against the
    // node's current config (central's mirror may be a version behind)
    showOnScreen: async (nodeId: string, outputId: string, inputId: string) => {
        const c = await call<Config>('GET', `/nodes/${nodeId}/api/config`)
        const o = c.outputs?.find((x) => x.id === outputId)
        if (!o) throw new Error('the screen is gone')
        return normCfg(
            await call<Config>(
                'PUT',
                `/nodes/${nodeId}/api/outputs/${outputId}`,
                { ...o, input_id: inputId },
                c.version,
            ),
        )
    },
    startNodeUpdate: (nodeId: string, version: string) =>
        call<UpdateStatus>('POST', `/nodes/${nodeId}/api/update`, { version }),
}
export interface SavedConfig {
    id: number
    name: string
    note: string
    source_node?: string
    source_version?: string
    created_by: string
    updated_at: string
    summary: { inputs: number; encodes: number; destinations: number; outputs: number; storage: number }
    doc?: Config
    media?: Record<string, string>
    hardware?: Hardware
}
// the machine a saved config is prepared for (its virtual node)
export interface Hardware {
    cpu_model?: string
    cores: number
    gpus: { name: string; nvenc_max: number }[] // nvenc_max 0 = no limit
    decklink: { name: string; input: boolean; output: boolean }[]
}
// something about a node the config does not fit: to be replaced (use:<value>) or ignored before it deploys
export interface Conflict {
    id: string
    message: string
    choices: { value: string; label: string }[]
    ignore_label: string
    resolution?: string
}
export interface NodeCheck {
    node: string
    name: string
    ok: boolean
    problems: string[]
    conflicts: Conflict[]
    warnings: string[]
    applied?: boolean
    error?: string
}
export interface NodeRelease {
    version: string
    added: string
    size: number
    features: string[] // what its nodes can; [] for a version from before the list
}
// central's deploys frozen (central freeze.go): until when and why; who is in Changes
export interface DeployFreeze {
    frozen: boolean
    until?: string
    note?: string
}
export interface ReleasesState {
    current: string
    floor: string // the oldest supported node version: never pruned
    releases: NodeRelease[]
}
export interface UpdateStatus {
    available: boolean
    upload: boolean // an uploaded bundle can be installed (central's key is pinned)
    current: string
    pending?: string
    hold?: UpdateHold // while held: an update is refused
    state?: 'running' | 'done' | 'failed'
    // while running: installing, then checking (the crash watch after the
    // install, up to 2 min) with its progress in detail
    phase?: 'installing' | 'checking'
    detail?: string
    version?: string
    from?: string
    started?: string
    finished?: string
    error?: string
    log?: string
}
export interface FFmpegBuild {
    id: string
    version: string
    sdk?: string
    created: string
    make_default: boolean
    status: 'queued' | 'building' | 'done' | 'failed' | 'aborted'
    phase?: string
    compiled: number
    started?: string
    compile_started?: string // when compiling began (builds since 2026-10-03)
    curve?: number[][] // [files compiled, seconds since compile_started], every 100 files
    finished?: string
    packages?: string
    size?: number
    live_bitrate?: boolean // built with ffmpeg-ese.patch (absent for builds from before it was recorded)
    recipe?: FFmpegRecipe // what it was built from (builds since 2026-10-08)
    error?: string
    aborting?: boolean
}
export interface FFmpegRecipe {
    id: string
    inputs: Record<string, string> // build-ffmpeg.sh, ffmpeg-ese.patch, decklink-profile.cpp: sha256; ubuntu: release
}
export interface FFmpegState {
    enabled: boolean
    default: string
    sdk: string
    latest: string
    builds: FFmpegBuild[]
    recipe?: FFmpegRecipe // what the builder builds from now
    // the default is not what a build now would make
    outdated?: { reasons: string[]; version: string; decklink: boolean; pending?: string }
}
export interface VendorFile {
    name: string
    kind: 'sdk' | 'desktopvideo' | 'ndi'
    size: number
    modified: string
}
export interface VendorState {
    enabled: boolean
    files: VendorFile[]
    nvidia: string
}
export interface UserBody {
    email: string
    password: string
    superadmin: boolean
    groups: number[]
    sso_only?: boolean // signs in by single sign-on only (absent: unchanged)
    roles?: Record<string, 'viewer' | 'editor'> // by group id; a group not named: editor
}
export interface ApiToken {
    id: number
    name: string
    expires_at: string | null
    created_at: string
    last_used: string | null
    scope: 'full' | 'monitoring' | 'companion'
    token?: string
}

// What the edit drawer opens: an entity to change (id) or to add (no id).
export type EditTarget =
    | { kind: 'input'; id?: string }
    | { kind: 'encode'; id?: string }
    | { kind: 'destination'; id?: string; encodeId?: string }
    | { kind: 'group'; id?: string }
    | { kind: 'output'; id?: string; inputId?: string }
    | { kind: 'storage'; id?: string }
    | { kind: 'node' }

// --- alerting (central, alerting.go) ---
export interface AlertMatch {
    groups?: number[]
    nodes?: string[]
    kinds?: string[] // input, encode, destination, output, machine, storage, node
    min_severity?: string // '' (warning or error), 'warning', 'error'
}
export interface AlertChannel {
    id: number
    name: string
    kind: string // webhook | slack | email
    // url and secret are shown as the secret placeholder once saved; email: url is the SMTP server
    config: { url: string; secret?: string; from?: string; to?: string }
    enabled: boolean
    last_ok: string | null
    last_error: string
    created_by: string
    created_at: string
}
export type AlertChannelBody = Pick<AlertChannel, 'name' | 'kind' | 'config' | 'enabled'> & { id?: number }
export interface AlertRule {
    id: number
    name: string
    enabled: boolean
    match: AlertMatch
    channel_ids: number[]
    for_seconds: number
    repeat_seconds: number
    notify_resolve: boolean
    updated_by: string
    updated_at: string
}
export type AlertRuleBody = Omit<AlertRule, 'id' | 'updated_by' | 'updated_at'> & { id?: number }
export interface MaintenanceWindow {
    id: number
    match: AlertMatch
    starts_at: string
    ends_at: string
    note: string
    created_by: string
    created_at: string
}
export interface AlertIncident {
    id: number
    rule_id: number
    rule_name: string
    node_id: string
    node_name: string
    kind: string
    entity: string
    entity_name: string
    state: string
    severity: string
    message: string
    opened_at: string
    notified_at: string | null
    resolved_at: string | null
    notify_error: string
}
